// src/components/admin/EmailBlast.jsx

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api.js';
import { toastSuccess, toastError } from '../../utils/toast.js';

/* ─────────────────────────────────────────────────────────────── */
/*  Constants                                                       */
/* ─────────────────────────────────────────────────────────────── */

const TARGET_GROUPS = [
  { value: 'all',      label: 'All Users' },
  { value: 'active',   label: 'Active'    },
  { value: 'paid',     label: 'Paid'      },
  { value: 'free',     label: 'Free'      },
  { value: 'waitlist', label: 'Waitlist'  },
];

const STATUS_META = {
  sent:    { bg: 'rgba(46,204,138,.13)',  color: '#2ecc8a', dot: '#2ecc8a',  label: 'Sent'    },
  partial: { bg: 'rgba(234,179,8,.13)',   color: '#eab308', dot: '#eab308',  label: 'Partial' },
  sending: { bg: 'rgba(59,130,246,.13)',  color: '#60a5fa', dot: '#60a5fa',  label: 'Sending' },
  failed:  { bg: 'rgba(217,79,79,.13)',   color: '#f87171', dot: '#f87171',  label: 'Failed'  },
  pending: { bg: 'rgba(255,255,255,.05)', color: '#888',    dot: '#555',     label: 'Pending' },
};

const TEMPLATES = [
  {
    id: 'new_feature', label: '🚀 New Feature',
    subject: 'Something new just dropped 🔥',
    previewText: "We've been building something for you — here's what's new.",
    headline: 'Something new is here.',
    body: "Hi {{name}},\n\nWe've been working hard behind the scenes at KFX Journal, and we're excited to share what's new.\n\nOur latest update brings smarter trade tracking, improved AI insights, and a cleaner dashboard to help you perform at your best.\n\nWhether you're a seasoned trader or just getting started — these updates are built for you.",
    ctaText: "Explore what's new",
    ctaUrl: 'https://kfxjournal.com/dashboard',
    footerNote: "You're receiving this because you opted in to KFX Journal updates.",
  },
  {
    id: 'welcome', label: '👋 Welcome',
    subject: 'Welcome to KFX Journal 🎉',
    previewText: 'Your trading journey starts here.',
    headline: 'Welcome aboard.',
    body: "Hi {{name}},\n\nWelcome to KFX Journal — we're thrilled to have you.\n\nYou now have access to a powerful trade journal, AI-powered insights, and a community of traders just like you.\n\nHere's what to do first: set up your trading profile, log your first trade, and let the AI start learning your patterns.",
    ctaText: 'Set up your profile',
    ctaUrl: 'https://kfxjournal.com/dashboard',
    footerNote: "You're receiving this because you created a KFX Journal account.",
  },
  {
    id: 'promo', label: '💰 Promotion',
    subject: 'Exclusive offer inside — just for you',
    previewText: 'A special offer we put together for our community.',
    headline: 'A special offer, just for you.',
    body: "Hi {{name}},\n\nAs a valued member of the KFX Journal community, we want to give you something special.\n\nFor a limited time, we're offering an exclusive deal available only to our existing users.\n\nDon't miss out — this offer expires soon.",
    ctaText: 'Claim your offer',
    ctaUrl: 'https://kfxjournal.com/dashboard',
    footerNote: "You're receiving this because you opted in to KFX Journal promotions.",
  },
  {
    id: 'premium_launch', label: '🎉 Premium Launched',
    subject: 'It’s here — Premium is officially live 🎉',
    previewText: 'The wait is over. Premium is ready for you.',
    headline: 'Premium is live.',
    body: "Hi {{name}},\n\nThanks for joining the waitlist — we're excited to let you know that KFX Journal Premium is officially live!\n\nAs an early supporter, you get first access to advanced analytics, unlimited AI insights, and priority support.\n\nUpgrade now to unlock the full experience and take your trading to the next level.",
    ctaText: 'Upgrade to Premium',
    ctaUrl: 'https://kfxjournal.com/pricing',
    footerNote: "You're receiving this because you joined the KFX Journal Premium waitlist.",
  },
  {
    id: 'blank', label: '✏️ Blank',
    subject: '', previewText: '', headline: '', body: '',
    ctaText: 'Visit KFX Journal',
    ctaUrl: 'https://kfxjournal.com/dashboard',
    footerNote: "You're receiving this because you opted in to KFX Journal updates.",
  },
];

/* ─────────────────────────────────────────────────────────────── */
/*  Email HTML builder                                              */
/* ─────────────────────────────────────────────────────────────── */

function buildEmailHTML({ headline, body, ctaText, ctaUrl, footerNote, previewText }) {
  const paragraphs = body
    .split('\n\n').filter(p => p.trim())
    .map(p => `<p style="margin:0 0 16px;font-size:15px;line-height:1.75;color:#3a3a3a;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">${p.replace(/\n/g,' ')}</p>`)
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="X-UA-Compatible" content="IE=edge"></head>
<body style="margin:0;padding:0;background:#f4f4f0;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
${previewText ? `<div style="display:none;max-height:0;overflow:hidden;font-size:1px;line-height:1px;color:#f4f4f0;">${previewText}&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;</div>` : ''}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f0;padding:32px 0;">
  <tr><td align="center">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">
      <tr><td style="padding:0 0 20px;text-align:center;">
        <span style="font-size:20px;font-weight:700;letter-spacing:-.5px;color:#1a1a1a;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">KFX<span style="color:#c9a227;">Journal</span></span>
      </td></tr>
      <tr><td style="background:#1a1a1a;border-radius:16px 16px 0 0;padding:48px 40px 36px;text-align:center;">
        <p style="margin:0 0 10px;font-size:11px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:#c9a227;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">KFX Journal</p>
        <h1 style="margin:0;font-size:30px;font-weight:700;line-height:1.2;color:#fff;letter-spacing:-.5px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">${headline || 'News from KFX Journal'}</h1>
      </td></tr>
      <tr><td style="background:#fff;padding:36px 40px 28px;">
        ${paragraphs || '<p style="margin:0 0 16px;font-size:15px;line-height:1.75;color:#3a3a3a;">Hi {{name}},</p>'}
        <table role="presentation" cellpadding="0" cellspacing="0" style="margin:28px 0 8px;">
          <tr><td style="background:#c9a227;border-radius:8px;">
            <a href="${ctaUrl}" target="_blank" style="display:inline-block;padding:14px 32px;font-size:15px;font-weight:600;color:#1a1a1a;text-decoration:none;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">${ctaText || 'Visit KFX Journal'} &rarr;</a>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="background:#c9a227;height:3px;"></td></tr>
      <tr><td style="background:#f4f4f0;padding:24px 40px;border-radius:0 0 16px 16px;">
        <p style="margin:0 0 8px;font-size:12px;color:#888;line-height:1.6;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">${footerNote}</p>
        <p style="margin:0;font-size:12px;color:#aaa;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
          <a href="{{unsubscribeUrl}}" style="color:#888;text-decoration:underline;">Unsubscribe</a>
          &nbsp;&middot;&nbsp; KFX Journal &nbsp;&middot;&nbsp; Kigali, Rwanda
        </p>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;
}

/* ─────────────────────────────────────────────────────────────── */
/*  Small shared components                                         */
/* ─────────────────────────────────────────────────────────────── */

function Label({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: 6 }}>
      {children}
    </div>
  );
}

function Pill({ children, active, gold, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 5, cursor: 'pointer', fontSize: 11.5,
      fontWeight: active ? 700 : 500, fontFamily: "'Inter',sans-serif",
      border: active
        ? (gold ? '1px solid rgba(212,168,67,.45)' : '1px solid rgba(212,168,67,.45)')
        : '1px solid rgba(255,255,255,.08)',
      background: active ? 'rgba(212,168,67,.1)' : 'rgba(255,255,255,.03)',
      color: active ? 'var(--gold)' : 'rgba(245,240,232,.45)',
      transition: 'all .15s',
    }}>
      {children}
    </button>
  );
}

function DeleteConfirmModal({ blast, onConfirm, onCancel, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.1)',
        borderRadius: 12, padding: '28px 28px 24px', maxWidth: 400, width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,.6)',
      }}>
        {/* Icon */}
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(217,79,79,.12)', border: '1px solid rgba(217,79,79,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
          </svg>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>Delete Campaign?</div>
        <div style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.6, marginBottom: 24 }}>
          This will permanently delete <span style={{ color: 'var(--white)', fontWeight: 600 }}>"{blast.subject}"</span>. This action cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: '10px', borderRadius: 6, cursor: 'pointer',
            background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
            color: 'var(--gray)', fontSize: 13, fontWeight: 600, fontFamily: "'Inter',sans-serif",
          }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={deleting} style={{
            flex: 1, padding: '10px', borderRadius: 6, cursor: deleting ? 'not-allowed' : 'pointer',
            background: deleting ? 'rgba(217,79,79,.05)' : 'rgba(217,79,79,.12)',
            border: '1px solid rgba(217,79,79,.35)',
            color: deleting ? 'rgba(248,113,113,.4)' : 'var(--red)',
            fontSize: 13, fontWeight: 700, fontFamily: "'Inter',sans-serif",
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {deleting ? (
              <>
                <svg style={{ animation: 'spin 1s linear infinite' }} width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70"/>
                </svg>
                Deleting…
              </>
            ) : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Main component                                                  */
/* ─────────────────────────────────────────────────────────────── */

const iStyle = (focused, field) => ({
  width: '100%', background: 'rgba(255,255,255,.04)',
  border: focused === field ? '1px solid rgba(212,168,67,.5)' : '1px solid rgba(255,255,255,.08)',
  borderRadius: 7, padding: '9px 12px', fontSize: 13, color: 'var(--white)',
  fontFamily: "'Inter',sans-serif", outline: 'none', boxSizing: 'border-box',
  transition: 'border-color .15s',
});

export default function EmailBlast() {
  /* composer state */
  const [targetGroup,  setTargetGroup]  = useState('all');
  const [activeTab,    setActiveTab]    = useState('compose');
  const [selectedTpl,  setSelectedTpl]  = useState(TEMPLATES[0].id);
  const [subject,      setSubject]      = useState(TEMPLATES[0].subject);
  const [previewText,  setPreviewText]  = useState(TEMPLATES[0].previewText);
  const [headline,     setHeadline]     = useState(TEMPLATES[0].headline);
  const [body,         setBody]         = useState(TEMPLATES[0].body);
  const [ctaText,      setCtaText]      = useState(TEMPLATES[0].ctaText);
  const [ctaUrl,       setCtaUrl]       = useState(TEMPLATES[0].ctaUrl);
  const [footerNote,   setFooterNote]   = useState(TEMPLATES[0].footerNote);
  const [focused,      setFocused]      = useState(null);
  const [sending,      setSending]      = useState(false);

  /* history state */
  const [history,      setHistory]      = useState([]);
  const [loadingHist,  setLoadingHist]  = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // blast to confirm delete
  const [deleting,     setDeleting]     = useState(false);

  /* responsive: collapse to single col below 900px */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => { fetchHistory(); }, []);

  /* ── helpers ─── */
  function applyTemplate(id) {
    const tpl = TEMPLATES.find(t => t.id === id);
    if (!tpl) return;
    setSelectedTpl(id);
    setSubject(tpl.subject);
    setPreviewText(tpl.previewText);
    setHeadline(tpl.headline);
    setBody(tpl.body);
    setCtaText(tpl.ctaText);
    setCtaUrl(tpl.ctaUrl);
    setFooterNote(tpl.footerNote);
  }

  async function fetchHistory() {
    try {
      setLoadingHist(true);
      const data = await adminApi.getEmailBlastHistory();
      setHistory(data.blasts || []);
    } catch (err) {
      console.error('Failed to load blast history', err);
    } finally {
      setLoadingHist(false);
    }
  }

  async function handleSend() {
    if (!subject.trim()) return toastError('Please enter a subject line');
    if (!headline.trim() && !body.trim()) return toastError('Please enter a headline or body');
    try {
      setSending(true);
      const htmlBody = buildEmailHTML({ headline, body, ctaText, ctaUrl, footerNote, previewText });
      const data = await adminApi.sendEmailBlast({
        subject: subject.trim(), htmlBody,
        textBody: body.replace(/<[^>]+>/g, ''),
        targetGroup,
      });
      toastSuccess(`✓ Blast queued for ${data.recipientCount} recipient${data.recipientCount !== 1 ? 's' : ''}`);
      applyTemplate('blank');
      fetchHistory();
    } catch (err) {
      toastError(err?.message || 'Failed to send blast');
    } finally {
      setSending(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await adminApi.deleteEmailBlast(deleteTarget.id);
      setHistory(h => h.filter(b => b.id !== deleteTarget.id));
      toastSuccess('Campaign deleted');
    } catch (err) {
      toastError(err?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  const previewHTML = buildEmailHTML({ headline, body, ctaText, ctaUrl, footerNote, previewText });

  /* ─────────────────────────────────────────────────────────── */
  /*  Left panel: composer                                        */
  /* ─────────────────────────────────────────────────────────── */
  const composerPanel = (
    <div style={{
      background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.07)',
      borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 22px 14px', borderBottom: '1px solid rgba(255,255,255,.06)',
        background: 'rgba(255,255,255,.02)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 3 }}>
            Programs · Email Blast
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.02em' }}>
            Campaign Composer
          </div>
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 700,
          letterSpacing: '.08em', color: '#2ecc8a',
          background: 'rgba(46,204,138,.08)', border: '1px solid rgba(46,204,138,.2)',
          padding: '4px 10px', borderRadius: 20,
        }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2ecc8a' }} />
          READY
        </span>
      </div>

      <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>

        {/* Templates */}
        <div style={{ marginBottom: 20 }}>
          <Label>Start from template</Label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TEMPLATES.map(t => (
              <Pill key={t.id} active={selectedTpl === t.id} onClick={() => applyTemplate(t.id)}>
                {t.label}
              </Pill>
            ))}
          </div>
        </div>

        {/* Target group */}
        <div style={{ marginBottom: 20 }}>
          <Label>Target Group</Label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TARGET_GROUPS.map(g => (
              <Pill key={g.value} active={targetGroup === g.value} onClick={() => setTargetGroup(g.value)}>
                {g.label}
              </Pill>
            ))}
          </div>
        </div>

        {/* Compose / Preview tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,.06)', marginBottom: 18 }}>
          {[['compose', '✏️  Compose'], ['preview', '👁  Preview']].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: "'Inter',sans-serif",
              color: activeTab === tab ? 'var(--gold)' : 'var(--gray)',
              borderBottom: activeTab === tab ? '2px solid var(--gold)' : '2px solid transparent',
              marginBottom: -1, transition: 'color .15s',
            }}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'compose' ? (
          <>
            {/* Subject + preview text */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <Label>Subject Line</Label>
                <input value={subject} maxLength={100} onChange={e => setSubject(e.target.value)}
                  onFocus={() => setFocused('subj')} onBlur={() => setFocused(null)}
                  placeholder="e.g. New features just dropped 🚀"
                  style={iStyle(focused, 'subj')} />
                <div style={{ fontSize: 9, color: 'var(--gray)', textAlign: 'right', marginTop: 3 }}>{subject.length}/100</div>
              </div>
              <div>
                <Label>Inbox Snippet</Label>
                <input value={previewText} maxLength={140} onChange={e => setPreviewText(e.target.value)}
                  onFocus={() => setFocused('prev')} onBlur={() => setFocused(null)}
                  placeholder="Short line shown before opening"
                  style={iStyle(focused, 'prev')} />
                <div style={{ fontSize: 9, color: 'var(--gray)', textAlign: 'right', marginTop: 3 }}>{previewText.length}/140</div>
              </div>
            </div>

            {/* Headline */}
            <div style={{ marginBottom: 14 }}>
              <Label>Hero Headline</Label>
              <input value={headline} maxLength={80} onChange={e => setHeadline(e.target.value)}
                onFocus={() => setFocused('hl')} onBlur={() => setFocused(null)}
                placeholder="Big bold line at top of email"
                style={iStyle(focused, 'hl')} />
            </div>

            {/* Body */}
            <div style={{ marginBottom: 14 }}>
              <Label>Body <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 9 }}>(blank line = new paragraph · {'{{name}}'} supported)</span></Label>
              <textarea rows={6} value={body} maxLength={3000} onChange={e => setBody(e.target.value)}
                onFocus={() => setFocused('body')} onBlur={() => setFocused(null)}
                placeholder={"Hi {{name}},\n\nWe've just launched something new…"}
                style={{ ...iStyle(focused, 'body'), resize: 'vertical', lineHeight: 1.65 }} />
              <div style={{ fontSize: 9, color: 'var(--gray)', textAlign: 'right', marginTop: 3 }}>{body.length}/3000</div>
            </div>

            {/* CTA row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 12, marginBottom: 14 }}>
              <div>
                <Label>Button Text</Label>
                <input value={ctaText} onChange={e => setCtaText(e.target.value)}
                  onFocus={() => setFocused('ctat')} onBlur={() => setFocused(null)}
                  placeholder="Explore what's new"
                  style={iStyle(focused, 'ctat')} />
              </div>
              <div>
                <Label>Button URL</Label>
                <input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)}
                  onFocus={() => setFocused('ctau')} onBlur={() => setFocused(null)}
                  placeholder="https://kfxjournal.com/dashboard"
                  style={iStyle(focused, 'ctau')} />
              </div>
            </div>

            {/* Footer note */}
            <div style={{ marginBottom: 4 }}>
              <Label>Footer Note</Label>
              <input value={footerNote} onChange={e => setFooterNote(e.target.value)}
                onFocus={() => setFocused('foot')} onBlur={() => setFocused(null)}
                placeholder="You're receiving this because…"
                style={iStyle(focused, 'foot')} />
            </div>
          </>
        ) : (
          /* Preview tab */
          <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,.07)' }}>
            <div style={{ background: 'var(--dark3)', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
              <div style={{ display: 'flex', gap: 5 }}>
                {['#ff5f57','#febc2e','#28c840'].map(c => <span key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c, display: 'inline-block' }} />)}
              </div>
              <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--white)' }}>{subject || '(no subject)'}</span>
            </div>
            <div style={{ padding: '7px 14px', borderBottom: '1px solid rgba(255,255,255,.04)', background: 'var(--dark3)', display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--white)', whiteSpace: 'nowrap' }}>KFX Journal</span>
              <span style={{ fontSize: 11, color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {subject} — {previewText || body.replace(/\n/g,' ').slice(0, 80)}
              </span>
            </div>
            <div style={{ background: '#f4f4f0', maxHeight: 420, overflowY: 'auto' }}>
              <div dangerouslySetInnerHTML={{ __html: previewHTML.replace(/{{name}}/g,'Alex').replace(/{{unsubscribeUrl}}/g,'#') }} />
            </div>
          </div>
        )}
      </div>

      {/* Send bar */}
      <div style={{
        padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,.06)',
        background: 'rgba(255,255,255,.02)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 12, color: 'var(--gray)', display: 'flex', alignItems: 'center', gap: 7 }}>
          Sending to
          <span style={{ background: 'rgba(212,168,67,.1)', border: '1px solid rgba(212,168,67,.2)', color: 'var(--gold)', fontSize: 9, fontWeight: 700, letterSpacing: '.06em', padding: '3px 10px', borderRadius: 4, textTransform: 'uppercase' }}>
            {targetGroup}
          </span>
          <span style={{ color: 'rgba(255,255,255,.2)' }}>·</span>
          <span style={{ fontSize: 11 }}>opted-in only</span>
        </div>
        <button onClick={handleSend} disabled={sending} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px',
          background: sending ? 'rgba(212,168,67,.05)' : 'rgba(212,168,67,.12)',
          border: '1px solid rgba(212,168,67,.35)',
          color: sending ? 'rgba(212,168,67,.4)' : 'var(--gold)',
          fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
          fontFamily: "'Inter',sans-serif", borderRadius: 7,
          cursor: sending ? 'not-allowed' : 'pointer', transition: 'all .15s',
        }}>
          {sending ? (
            <><svg style={{ animation: 'spin 1s linear infinite' }} width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70"/></svg>Sending…</>
          ) : (
            <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>Send Blast</>
          )}
        </button>
      </div>
    </div>
  );

  /* ─────────────────────────────────────────────────────────── */
  /*  Right panel: campaign history                              */
  /* ─────────────────────────────────────────────────────────── */
  const historyPanel = (
    <div style={{
      background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.07)',
      borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 22px 14px', borderBottom: '1px solid rgba(255,255,255,.06)',
        background: 'rgba(255,255,255,.02)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.01em' }}>
            <span style={{ color: 'var(--gold)' }}>Campaign</span> History
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
            {history.length} campaign{history.length !== 1 ? 's' : ''} sent
          </div>
        </div>
        <button onClick={fetchHistory} title="Refresh" style={{
          width: 32, height: 32, borderRadius: 7, border: '1px solid rgba(255,255,255,.08)',
          background: 'rgba(255,255,255,.03)', cursor: 'pointer', color: 'var(--gray)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loadingHist ? (
          <div style={{ padding: '48px 22px', textAlign: 'center' }}>
            <svg style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="rgba(212,168,67,.3)" strokeWidth="3"/>
              <circle cx="12" cy="12" r="10" stroke="var(--gold)" strokeWidth="3" strokeDasharray="30 70"/>
            </svg>
            <div style={{ fontSize: 12, color: 'var(--gray)' }}>Loading campaigns…</div>
          </div>
        ) : history.length === 0 ? (
          <div style={{ padding: '64px 22px', textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(212,168,67,.06)', border: '1px solid rgba(212,168,67,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,67,.4)" strokeWidth="1.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 6 }}>No campaigns yet</div>
            <div style={{ fontSize: 12, color: 'var(--gray)' }}>Send your first blast from the composer</div>
          </div>
        ) : (
          history.map((blast, idx) => {
            const sc = STATUS_META[blast.status] || STATUS_META.pending;
            const date = new Date(blast.sentAt || blast.createdAt);
            return (
              <div key={blast.id} style={{
                padding: '14px 22px',
                borderBottom: idx < history.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none',
                transition: 'background .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.025)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {/* Row top: subject + delete btn */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
                      {blast.subject}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray)' }}>
                      {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {' · '}
                      {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <button
                    onClick={() => setDeleteTarget(blast)}
                    title="Delete campaign"
                    style={{
                      width: 30, height: 30, borderRadius: 6, flexShrink: 0,
                      border: '1px solid rgba(217,79,79,.2)',
                      background: 'rgba(217,79,79,.07)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--red)', transition: 'all .15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(217,79,79,.16)'; e.currentTarget.style.borderColor = 'rgba(217,79,79,.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(217,79,79,.07)'; e.currentTarget.style.borderColor = 'rgba(217,79,79,.2)'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                    </svg>
                  </button>
                </div>

                {/* Row bottom: stats badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {/* Status */}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: sc.bg, color: sc.color, fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />
                    {sc.label}
                  </span>
                  {/* Group */}
                  <span style={{ background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.15)', color: 'var(--gold)', fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4 }}>
                    {blast.targetGroup}
                  </span>
                  {/* Sent count */}
                  <span style={{ fontSize: 11, color: 'var(--gray)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.12 1.2 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.45-.45a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0120 15"/></svg>
                    <span style={{ color: 'var(--white)', fontWeight: 600 }}>{blast.sentCount.toLocaleString()}</span> sent
                  </span>
                  {/* Failed count */}
                  {blast.failedCount > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                      <span style={{ fontWeight: 600 }}>{blast.failedCount}</span> failed
                    </span>
                  )}
                  {/* Recipients */}
                  <span style={{ fontSize: 11, color: 'var(--gray)', marginLeft: 'auto' }}>
                    {blast.recipientCount} recipient{blast.recipientCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  /* ─────────────────────────────────────────────────────────── */
  /*  Render                                                      */
  /* ─────────────────────────────────────────────────────────── */
  return (
    <div style={{ padding: isMobile ? '16px' : '24px 28px', fontFamily: "'Inter',sans-serif" }}>

      {/* Two-col layout, collapses on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '480px 1fr',
        gap: 18,
        alignItems: 'start',
      }}>
        {composerPanel}
        {historyPanel}
      </div>

      {/* Delete confirm modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          blast={deleteTarget}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}